import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, request } from '@/api/client';
import type {
  ApplicationPage,
  CareersOptions,
  JobShare,
  JobShareCreate,
  Vacancy,
  VacancyAction,
  VacancyBulkDelete,
  VacancyBulkDeleteResult,
  VacancyPage,
  VacancyState,
  VacancyWrite,
  VacancyImportPreview,
  VacancyImportResult,
} from '@/api/schema';
import { CANDIDATES_KEY } from '@/features/candidates/api';

export const CAREERS_KEY = ['careers'] as const;

export type VacancyFilters = {
  page: number;
  search: string;
  industry: string;
  country: string;
  state: VacancyState | '';
  available?: boolean;
};

export function useVacancies(filters: VacancyFilters) {
  return useQuery({
    queryKey: [...CAREERS_KEY, 'vacancies', filters],
    queryFn: () =>
      api.get<VacancyPage>('/api/v1/careers/vacancies', {
        query: { ...filters, per_page: 12 },
      }),
  });
}

export function useCareersOptions() {
  return useQuery({
    queryKey: [...CAREERS_KEY, 'options'],
    queryFn: () => api.get<CareersOptions>('/api/v1/careers/options'),
  });
}

export function useSaveVacancy() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, values }: { id: string | undefined; values: VacancyWrite }) =>
      id
        ? request<Vacancy>(`/api/v1/careers/vacancies/${id}`, { method: 'PUT', body: values })
        : api.post<Vacancy>('/api/v1/careers/vacancies', values),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: CAREERS_KEY }),
        client.invalidateQueries({ queryKey: ['public-jobs'] }),
        client.invalidateQueries({ queryKey: ['public-job'] }),
      ]);
    },
  });
}

export function useVacancyAction() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: VacancyAction['action'] }) =>
      action === 'delete'
        ? api.delete(`/api/v1/careers/vacancies/${id}`)
        : request<Vacancy>(`/api/v1/careers/vacancies/${id}/state`, {
            method: 'PATCH',
            body: { state: action === 'deactivate' ? 'closed' : 'published' },
          }),
    retry: false,
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: CAREERS_KEY }),
        client.invalidateQueries({ queryKey: ['public-jobs'] }),
        client.invalidateQueries({ queryKey: ['public-job'] }),
      ]);
    },
  });
}

export function useBulkDeleteVacancies() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payload: VacancyBulkDelete) =>
      api.post<VacancyBulkDeleteResult>('/api/v1/careers/vacancies/bulk/delete', payload),
    retry: false,
    onSettled: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: CAREERS_KEY }),
        client.invalidateQueries({ queryKey: ['public-jobs'] }),
        client.invalidateQueries({ queryKey: ['public-job'] }),
      ]);
    },
  });
}

export function useJobShares(candidateId: string) {
  return useQuery({
    queryKey: [...CAREERS_KEY, 'shares', candidateId],
    queryFn: () => api.get<JobShare[]>(`/api/v1/careers/candidates/${candidateId}/shares`),
  });
}

export function useSendJobProfiles(candidateId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (values: JobShareCreate) =>
      api.post<JobShare>(`/api/v1/careers/candidates/${candidateId}/shares`, values),
    retry: false,
    onSettled: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: [...CAREERS_KEY, 'shares', candidateId] }),
        client.invalidateQueries({ queryKey: CANDIDATES_KEY }),
        client.invalidateQueries({ queryKey: ['dashboard'] }),
        client.invalidateQueries({ queryKey: ['notifications'] }),
      ]);
    },
  });
}

export function useApplications(page: number, candidateId?: string) {
  return useQuery({
    queryKey: [...CAREERS_KEY, 'applications', page, candidateId],
    queryFn: () =>
      api.get<ApplicationPage>('/api/v1/careers/applications', {
        query: { page, per_page: 20, candidate_id: candidateId },
      }),
  });
}

export function useVacancyImportPreview() {
  return useMutation({
    mutationFn: (file: File) => {
      const body = new FormData();
      body.append('file', file);
      return api.upload<VacancyImportPreview>('/api/v1/careers/vacancies/import/preview', body);
    },
    retry: false,
  });
}

export function useVacancyImportCommit() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const body = new FormData();
      body.append('file', file);
      return api.upload<VacancyImportResult>('/api/v1/careers/vacancies/import/commit', body);
    },
    retry: false,
    onSettled: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: CAREERS_KEY }),
        client.invalidateQueries({ queryKey: ['public-jobs'] }),
      ]);
    },
  });
}
